import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import { ColorDot } from '@/components/ui/color-dot'
import { useRenameCategory, useUpdateCategoryParent, useSaveBudgetPair } from '@/lib/hooks'
import type { Category, Budget } from '@/lib/hooks'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: Category | null
  parentCategories: Category[]
  onDelete: (category: Category) => void
  householdBudget?: Budget
  personalBudget?: Budget
  parentHasHouseholdBudget: boolean
  parentHasPersonalBudget: boolean
  userId: string
}

export function SubcategoryEditSheet({
  open,
  onOpenChange,
  category,
  parentCategories,
  onDelete,
  householdBudget,
  personalBudget,
  parentHasHouseholdBudget,
  parentHasPersonalBudget,
  userId,
}: Props) {
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState('')
  const [selectOpen, setSelectOpen] = useState(false)
  const [householdAmount, setHouseholdAmount] = useState('')
  const [personalAmount, setPersonalAmount] = useState('')

  const renameCat = useRenameCategory()
  const updateParent = useUpdateCategoryParent()
  const saveBudgetPair = useSaveBudgetPair()

  useEffect(() => {
    if (open && category) {
      setName(category.name)
      setParentId(category.parent_id ?? '')
      setHouseholdAmount(householdBudget ? String(householdBudget.amount) : '')
      setPersonalAmount(personalBudget ? String(personalBudget.amount) : '')
    }
  }, [open, category, householdBudget, personalBudget])

  const handleSave = () => {
    if (!category || !name.trim()) return
    const nameChanged = name.trim() !== category.name
    const parentChanged = parentId !== category.parent_id
    const promises: Promise<unknown>[] = []
    if (nameChanged) {
      promises.push(
        new Promise((resolve, reject) =>
          renameCat.mutate(
            { id: category.id, name: name.trim() },
            { onSuccess: resolve, onError: reject },
          )
        )
      )
    }
    if (parentChanged && parentId) {
      promises.push(
        new Promise((resolve, reject) =>
          updateParent.mutate(
            { id: category.id, parentId },
            { onSuccess: resolve, onError: reject },
          )
        )
      )
    }

    // Save budget if changed (skip locked scopes; personal categories cannot have household budgets)
    const isPersonal = !!category.owner_id
    const hAmount = isPersonal
      ? null
      : parentHasHouseholdBudget ? (householdBudget?.amount ?? null) : (householdAmount.trim() ? Number(householdAmount) : null)
    const pAmount = parentHasPersonalBudget ? (personalBudget?.amount ?? null) : (personalAmount.trim() ? Number(personalAmount) : null)
    const hChanged = !isPersonal && !parentHasHouseholdBudget && hAmount !== (householdBudget?.amount ?? null)
    const pChanged = !parentHasPersonalBudget && pAmount !== (personalBudget?.amount ?? null)

    if (hChanged || pChanged) {
      promises.push(
        new Promise((resolve, reject) =>
          saveBudgetPair.mutate(
            {
              household: { budgets: [{ category_id: category.id, amount: hAmount }] },
              personal: { budgets: [{ category_id: category.id, amount: pAmount }], userId },
            },
            { onSuccess: () => resolve(undefined), onError: reject },
          )
        )
      )
    }

    if (promises.length === 0) {
      onOpenChange(false)
      return
    }
    Promise.all(promises).then(() => onOpenChange(false))
  }

  const isPending = renameCat.isPending || updateParent.isPending || saveBudgetPair.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} dragDisabled={selectOpen}>
        <SheetHeader>
          <SheetTitle>カテゴリを編集</SheetTitle>
          <SheetDescription>
            カテゴリの名前・分類・予算を変更できます。
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="sub-category-name">カテゴリ名</Label>
            <Input
              id="sub-category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoCapitalize="off"
              enterKeyHint="done"
            />
          </div>
          <div className="space-y-2">
            <Label>分類</Label>
            <Select value={parentId} onValueChange={setParentId} onOpenChange={setSelectOpen}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {parentCategories.filter((p) => !p.is_hidden).map((parent) => (
                  <SelectItem key={parent.id} value={parent.id}>
                    {parent.color && <ColorDot color={parent.color} />}
                    {parent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>公開範囲</Label>
            <Select value={category?.owner_id ? 'personal' : 'household'} disabled>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="household">家計</SelectItem>
                <SelectItem value="personal">個人</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              公開範囲はカテゴリ作成時に決まるため変更できません。
            </p>
          </div>

          {!category?.owner_id && (
            <div className="space-y-2">
              <Label htmlFor="sub-budget-household">予算（家計）</Label>
              {parentHasHouseholdBudget ? (
                <p className="text-xs text-muted-foreground">
                  親カテゴリに予算が設定されているため、個別設定できません。
                </p>
              ) : (
                <CurrencyInput
                  id="sub-budget-household"
                  value={householdAmount}
                  onChange={(e) => setHouseholdAmount(e.target.value)}
                  placeholder="未設定"
                />
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="sub-budget-personal">予算（個人）</Label>
            {parentHasPersonalBudget ? (
              <p className="text-xs text-muted-foreground">
                親カテゴリに予算が設定されているため、個別設定できません。
              </p>
            ) : (
              <CurrencyInput
                id="sub-budget-personal"
                value={personalAmount}
                onChange={(e) => setPersonalAmount(e.target.value)}
                placeholder="未設定"
              />
            )}
          </div>
        </div>
        <SheetFooter>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={handleSave}
              disabled={!name.trim() || isPending}
            >
              {isPending ? '保存中...' : '保存'}
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => {
                if (!category) return
                onDelete(category)
                onOpenChange(false)
              }}
            >
              削除
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
